import React, { useState, useEffect } from 'react';
import { Link2, Plus, Trash2, Copy, Check, ExternalLink, RefreshCw, Zap, AlertCircle, HelpCircle, Loader2, X } from 'lucide-react';
import { useChatbot } from '../../../contexts/ChatbotContext';
import { useDesignColors } from '../../../hooks/useDesignColors';
import { supabase } from '../../../lib/supabase';
import { toast } from 'react-hot-toast';

interface Webhook {
  id: string;
  name: string;
  url: string;
  provider: 'zapier' | 'make' | 'custom';
  events: string[];
  active: boolean;
  created_at: string;
}

export default function WebhooksIntegration() {
  const { chatbotPublicId } = useChatbot();
  const { colors } = useDesignColors();
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<Partial<Webhook>>({
    name: '',
    url: '',
    provider: 'zapier',
    events: ['new_lead', 'new_conversation'],
    active: true
  });
  const [copied, setCopied] = useState<string | null>(null);
  const [testingWebhook, setTestingWebhook] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{success: boolean, message: string} | null>(null);
  const [saving, setSaving] = useState(false);

  // Fetch webhooks
  useEffect(() => {
    if (!chatbotPublicId) return;
    
    const fetchWebhooks = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { data, error } = await supabase
          .from('webhooks')
          .select('*')
          .eq('chatbot_public_id', chatbotPublicId)
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        
        setWebhooks(data || []);
      } catch (err) {
        console.error('Error fetching webhooks:', err);
        setError(err instanceof Error ? err.message : 'Failed to load webhooks');
      } finally {
        setLoading(false);
      }
    };
    
    fetchWebhooks();
  }, [chatbotPublicId]);

  // Add webhook
  const handleAddWebhook = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatbotPublicId || !formData.name || !formData.url) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('webhooks')
        .insert({
          chatbot_public_id: chatbotPublicId,
          name: formData.name,
          url: formData.url,
          provider: formData.provider,
          events: formData.events,
          active: formData.active
        })
        .select()
        .single();
      
      if (error) throw error;
      
      setWebhooks(prev => [data, ...prev]);
      setShowAddForm(false);
      setFormData({
        name: '',
        url: '',
        provider: 'zapier',
        events: ['new_lead', 'new_conversation'],
        active: true
      });
      toast.success('Webhook added successfully');
    } catch (err) {
      console.error('Error adding webhook:', err);
      setError(err instanceof Error ? err.message : 'Failed to add webhook');
      toast.error('Failed to add webhook');
    } finally {
      setSaving(false);
    }
  };

  // Delete webhook
  const handleDeleteWebhook = async (id: string) => {
    if (!chatbotPublicId) return;
    
    if (!window.confirm('Are you sure you want to delete this webhook?')) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const { error } = await supabase
        .from('webhooks')
        .delete()
        .eq('id', id)
        .eq('chatbot_public_id', chatbotPublicId);
      
      if (error) throw error;
      
      setWebhooks(prev => prev.filter(webhook => webhook.id !== id));
    } catch (err) {
      console.error('Error deleting webhook:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete webhook');
    } finally {
      setLoading(false);
    }
  };

  // Toggle webhook active state
  const handleToggleWebhook = async (id: string, currentState: boolean) => {
    if (!chatbotPublicId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const { error } = await supabase
        .from('webhooks')
        .update({ active: !currentState })
        .eq('id', id)
        .eq('chatbot_public_id', chatbotPublicId);
      
      if (error) throw error;
      
      setWebhooks(prev => prev.map(webhook => 
        webhook.id === id ? { ...webhook, active: !currentState } : webhook
      ));
    } catch (err) {
      console.error('Error toggling webhook:', err);
      setError(err instanceof Error ? err.message : 'Failed to update webhook');
    } finally {
      setLoading(false);
    }
  };

  // Copy webhook URL
  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopied(url);
    setTimeout(() => setCopied(null), 2000);
  };

  // Test webhook
  const handleTestWebhook = async (id: string, url: string) => {
    if (!chatbotPublicId) return;
    
    try {
      setTestingWebhook(id);
      setTestResult(null);
      
      // Send a test payload to the webhook
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event: 'test',
          chatbot_id: chatbotPublicId,
          timestamp: new Date().toISOString(),
          data: {
            message: 'This is a test webhook from Botmaker Agency',
            test: true
          }
        })
      });
      
      if (response.ok) {
        setTestResult({
          success: true,
          message: 'Webhook test successful! The endpoint responded with status ' + response.status
        });
      } else {
        setTestResult({
          success: false,
          message: 'Webhook test failed. The endpoint responded with status ' + response.status
        });
      }
    } catch (err) {
      console.error('Error testing webhook:', err);
      setTestResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to test webhook'
      });
    } finally {
      setTimeout(() => {
        setTestingWebhook(null);
        setTimeout(() => setTestResult(null), 3000);
      }, 1000);
    }
  };

  // Get provider icon
  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'zapier':
        return <Zap className="w-5 h-5 text-orange-400" />;
      case 'make':
        return <RefreshCw className="w-5 h-5 text-blue-400" />;
      default:
        return <Link2 className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-500/20 rounded-lg">
              <Link2 className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white">Webhooks</h3>
              <p className="text-gray-400 text-sm mt-1">Connect your chatbot to external services</p>
            </div>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all duration-200 shadow-md"
          >
            <Plus className="w-4 h-4" />
            Add Webhook
          </button>
        </div>
        
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-lg mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}
        
        {testResult && (
          <div className={`${testResult.success ? 'bg-green-500/10 border-green-500/50 text-green-400' : 'bg-red-500/10 border-red-500/50 text-red-400'} p-4 rounded-lg mb-6 border flex items-start gap-3`}>
            {testResult.success ? 
              <Check className="w-5 h-5 mt-0.5 flex-shrink-0" /> : 
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            }
            <div>
              <p className="font-medium">{testResult.success ? 'Success' : 'Error'}</p>
              <p className="text-sm">{testResult.message}</p>
            </div>
          </div>
        )}
        
        {showAddForm && (
          <div className="bg-black/40 backdrop-blur-sm rounded-lg p-6 mb-6 border border-gray-700 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white font-medium text-lg">Add New Webhook</h4>
              <button 
                onClick={() => setShowAddForm(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleAddWebhook} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
                    Webhook Name
                    <HelpCircle className="w-3.5 h-3.5 text-gray-500" title="A descriptive name for your webhook" />
                  </label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-black/30 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                    placeholder="e.g., Zapier Lead Integration"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
                    Provider
                    <HelpCircle className="w-3.5 h-3.5 text-gray-500" title="Select the service you're connecting to" />
                  </label>
                  <select
                    value={formData.provider || 'zapier'}
                    onChange={(e) => setFormData({ ...formData, provider: e.target.value as 'zapier' | 'make' | 'custom' })}
                    className="w-full bg-black/30 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  >
                    <option value="zapier">Zapier</option>
                    <option value="make">Make (Integromat)</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
                  Webhook URL
                  <HelpCircle className="w-3.5 h-3.5 text-gray-500" title="The URL where webhook data will be sent" />
                </label>
                <input
                  type="url"
                  value={formData.url || ''}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className="w-full bg-black/30 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  placeholder="https://hooks.zapier.com/hooks/catch/..."
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3 flex items-center gap-1">
                  Events to Trigger Webhook
                  <HelpCircle className="w-3.5 h-3.5 text-gray-500" title="Select which events will trigger this webhook" />
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center px-4 py-3 bg-black/20 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors">
                    <input
                      id="event-new-lead"
                      type="checkbox"
                      checked={formData.events?.includes('new_lead') || false}
                      onChange={(e) => {
                        const events = formData.events || [];
                        if (e.target.checked) {
                          setFormData({ ...formData, events: [...events, 'new_lead'] });
                        } else {
                          setFormData({ ...formData, events: events.filter(event => event !== 'new_lead') });
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-700 bg-black/50 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label htmlFor="event-new-lead" className="ml-3 block text-sm text-gray-300">
                      <div className="font-medium">New Lead</div>
                      <div className="text-xs text-gray-500">When a user submits contact info</div>
                    </label>
                  </div>
                  
                  <div className="flex items-center px-4 py-3 bg-black/20 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors">
                    <input
                      id="event-new-conversation"
                      type="checkbox"
                      checked={formData.events?.includes('new_conversation') || false}
                      onChange={(e) => {
                        const events = formData.events || [];
                        if (e.target.checked) {
                          setFormData({ ...formData, events: [...events, 'new_conversation'] });
                        } else {
                          setFormData({ ...formData, events: events.filter(event => event !== 'new_conversation') });
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-700 bg-black/50 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label htmlFor="event-new-conversation" className="ml-3 block text-sm text-gray-300">
                      <div className="font-medium">New Conversation</div>
                      <div className="text-xs text-gray-500">When a conversation is started</div>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center px-4 py-3 bg-black/20 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors">
                <input
                  id="webhook-active"
                  type="checkbox"
                  checked={formData.active || false}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-700 bg-black/50 text-indigo-600 focus:ring-indigo-500"
                />
                <label htmlFor="webhook-active" className="ml-3 block text-sm text-gray-300">
                  <div className="font-medium">Active</div>
                  <div className="text-xs text-gray-500">Enable this webhook to receive data</div>
                </label>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-5 py-2.5 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !formData.name || !formData.url}
                  className="px-5 py-2.5 rounded-lg text-white flex items-center gap-2 transition-all"
                  style={{
                    background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                    opacity: saving || !formData.name || !formData.url ? 0.7 : 1
                  }}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>Add Webhook</>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
        
        {loading && webhooks.length === 0 ? (
          <div className="flex justify-center items-center py-16">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
              <p className="text-gray-400">Loading webhooks...</p>
            </div>
          </div>
        ) : webhooks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 bg-black/20 border border-dashed border-gray-700 rounded-xl">
            <div className="w-16 h-16 bg-gray-800/50 rounded-full flex items-center justify-center mb-4">
              <Link2 className="w-8 h-8 text-gray-500" />
            </div>
            <h4 className="text-xl font-medium text-gray-300 mb-2">No webhooks configured</h4>
            <p className="text-gray-500 text-center max-w-md mb-6">
              Add a webhook to send lead and conversation data to external services like Zapier, Make, or your custom endpoint.
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 px-5 py-2.5"
              style={{
                background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                borderRadius: '0.5rem',
              }}
            >
              <Plus className="w-4 h-4" />
              <span className="font-medium">Create Your First Webhook</span>
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {webhooks.map(webhook => (
              <div 
                key={webhook.id}
                className="bg-black/30 rounded-lg p-5 border border-gray-700 hover:border-gray-600 transition-all duration-200 shadow-md"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      webhook.provider === 'zapier' ? 'bg-orange-500/20' : 
                      webhook.provider === 'make' ? 'bg-blue-500/20' : 
                      'bg-purple-500/20'
                    }`}>
                      {getProviderIcon(webhook.provider)}
                    </div>
                    <div>
                      <h4 className="text-white font-medium">{webhook.name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          webhook.active 
                            ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                            : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                        }`}>
                          {webhook.active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="text-xs text-gray-500">
                          Created {new Date(webhook.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-auto">
                    <button
                      onClick={() => handleToggleWebhook(webhook.id, webhook.active)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 ${
                        webhook.active
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                      } transition-colors`}
                    >
                      {webhook.active ? 'Disable' : 'Enable'}
                    </button>
                    
                    <button
                      onClick={() => handleDeleteWebhook(webhook.id)}
                      className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                      title="Delete webhook"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="flex flex-col md:flex-row items-center gap-3 mb-4">
                  <div className="flex-1 bg-black/40 border border-gray-700 rounded-lg px-4 py-2.5 text-gray-400 text-sm overflow-hidden overflow-ellipsis whitespace-nowrap w-full">
                    {webhook.url}
                  </div>
                  
                  <div className="flex items-center gap-2 ml-auto w-full md:w-auto justify-end">
                    <button
                      onClick={() => handleCopyUrl(webhook.url)}
                      className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white transition-colors"
                      title="Copy URL"
                    >
                      {copied === webhook.url ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                    
                    <button
                      onClick={() => handleTestWebhook(webhook.id, webhook.url)}
                      disabled={testingWebhook === webhook.id}
                      className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Test webhook"
                    >
                      <RefreshCw className={`w-4 h-4 ${testingWebhook === webhook.id ? 'animate-spin' : ''}`} />
                    </button>
                    
                    <a 
                      href={webhook.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white transition-colors"
                      title="Open URL"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {webhook.events.map(event => (
                    <span 
                      key={event}
                      className="px-3 py-1.5 text-xs rounded-lg bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 font-medium"
                    >
                      {event === 'new_lead' ? 'New Lead' : 
                       event === 'new_conversation' ? 'New Conversation' : 
                       event}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Documentation */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg">
        <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-indigo-400" />
          Webhook Documentation
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-black/30 rounded-lg p-5 border border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Zap className="w-5 h-5 text-orange-400" />
              </div>
              <h4 className="text-lg font-medium text-white">Zapier Integration</h4>
            </div>
            <p className="text-gray-300 mb-4">
              Connect your chatbot to thousands of apps with Zapier. Follow these steps to set up a Zapier webhook:
            </p>
            <ol className="space-y-3 text-gray-300 mb-5">
              {[
                "Create a new Zap in Zapier",
                "Choose \"Webhook by Zapier\" as the trigger app",
                "Select \"Catch Hook\" as the trigger event",
                "Copy the webhook URL provided by Zapier",
                "Add a new webhook here with the Zapier URL",
                "Test the webhook to ensure it's working",
                "Set up the rest of your Zap to process the data"
              ].map((step, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-orange-500/20 text-orange-400 text-xs font-medium flex-shrink-0 mt-0.5">
                    {index + 1}
                  </div>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
            <a 
              href="https://zapier.com/apps/webhook/integrations" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 text-orange-400 rounded-lg hover:bg-orange-500/20 transition-colors"
            >
              Learn more about Zapier Webhooks
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
          
          <div className="bg-black/30 rounded-lg p-5 border border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <RefreshCw className="w-5 h-5 text-blue-400" />
              </div>
              <h4 className="text-lg font-medium text-white">Make (Integromat) Integration</h4>
            </div>
            <p className="text-gray-300 mb-4">
              Connect your chatbot to Make (formerly Integromat) to create powerful automation workflows:
            </p>
            <ol className="space-y-3 text-gray-300 mb-5">
              {[
                "Create a new scenario in Make",
                "Add a \"Webhooks\" module as the trigger",
                "Select \"Custom webhook\" as the webhook type",
                "Copy the webhook URL provided by Make",
                "Add a new webhook here with the Make URL",
                "Test the webhook to ensure it's working",
                "Configure the rest of your scenario to process the data"
              ].map((step, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-xs font-medium flex-shrink-0 mt-0.5">
                    {index + 1}
                  </div>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
            <a 
              href="https://www.make.com/en/help/tools/webhooks" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-colors"
            >
              Learn more about Make Webhooks
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
        
        <div className="mt-8 bg-black/30 rounded-lg p-5 border border-gray-700">
          <h4 className="text-lg font-medium text-white mb-4">Webhook Payload Format</h4>
          <p className="text-gray-300 mb-4">
            Here's an example of the payload that will be sent to your webhook:
          </p>
          <div className="relative">
            <pre className="bg-black/70 rounded-lg p-4 text-gray-300 text-sm overflow-x-auto border border-gray-700">
{`{
  "event": "new_lead",
  "chatbot_id": "${chatbotPublicId || 'your-chatbot-id'}",
  "timestamp": "2025-04-15T12:34:56.789Z",
  "data": {
    "lead": {
      "id": "uuid-of-lead",
      "type": "email",
      "value": "example@email.com",
      "created_at": "2025-04-15T12:34:56.789Z"
    },
    "conversation": {
      "id": "uuid-of-conversation",
      "message_count": 5,
      "duration_seconds": 120,
      "initial_message": "Hello, I'm interested in your services"
    }
  }
}`}
            </pre>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`{
  "event": "new_lead",
  "chatbot_id": "${chatbotPublicId || 'your-chatbot-id'}",
  "timestamp": "2025-04-15T12:34:56.789Z",
  "data": {
    "lead": {
      "id": "uuid-of-lead",
      "type": "email",
      "value": "example@email.com",
      "created_at": "2025-04-15T12:34:56.789Z"
    },
    "conversation": {
      "id": "uuid-of-conversation",
      "message_count": 5,
      "duration_seconds": 120,
      "initial_message": "Hello, I'm interested in your services"
    }
  }
}`);
                toast.success('Example payload copied to clipboard');
              }}
              className="absolute top-3 right-3 p-2 bg-gray-800 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
              title="Copy payload example"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}